import {
  useEffect,
  useState,
  useRef,
  useContext,
  type CSSProperties,
} from "react";
import { Box, Button, Chip, LinearProgress } from "@mui/material";
import * as Sentry from "@sentry/react";

import { FeedEntry, Medium } from "protogen/feed_pb";
import { useSendTextMessage } from "services/textMessage";
import {
  SendTextMessageRequest,
  SendTextMessageResponse,
} from "protogen/text_message_service_pb";
import { useUploader, FileUpload } from "../creation/FileUploader";
import { SendHorizontal as SendIcon } from "lucide-react";
import { CurrentUserContext } from "components/context/RequireAuth";
import { shouldLogForUser } from "common/debugLogging";

import {
  isMobileiOS,
  isStandaloneWebapp,
  isTouchDevice,
} from "../../common/utils";
import MessageMenu, { UploadSpeedDialAction } from "./MessageMenu";
import SpeedDialAction from "@mui/material/SpeedDialAction";
import { UploadAttachment } from "protogen/common_pb";
import { PlainMessage } from "@bufbuild/protobuf";
import DragNDrop from "../common/DragNDrop";
import SimpleEditor, { Handle } from "../editor/SimpleEditor";
import { RichContent, processImages } from "../editor/utils";
import useCached from "../hooks/useCached";
import { twilioMessageLength } from "../../common/smsUtils";
import { CalendarClock, SquareUserRound, Paperclip, X } from "lucide-react";
import ScheduledMessageDialog from "./ScheduledMessageDialog";
import UploadThumbnail from "./UploadThumbnail";
import useIsMobile from "components/hooks/useIsMobile";

interface SMSInputProps {
  onSent: (e: FeedEntry) => void;
  familyRef: string | null;
  recipientRefs: string[];
  placeholder: string;
  contactEnabled?: boolean;
  forceDisabled?: boolean;
  initialContent?: string;
  onUpdateContent?: () => void;
}

const SendButton = ({
  disabled,
  onClick,
  shouldLog,
  debugContext,
}: {
  disabled: boolean;
  onClick: () => void;
  shouldLog?: boolean;
  debugContext?: any;
}) => {
  const handleClick = () => {
    if (shouldLog && debugContext?.correlationId) {
      Sentry.logger.info("TextMessageCompose: SendButton clicked", {
        disabled,
        ...debugContext,
      });
    }
    onClick();
  };

  return (
    <Button
      disabled={disabled}
      onClick={handleClick}
      variant="text"
      sx={{
        minWidth: "24px",
        borderRadius: "8px",
        border: "none",
        padding: "0px",
        height: "44px",
        width: "24px",
        overflow: "hidden",
        opacity: 1,
        transition: "all 0.3s ease",
        marginLeft: 0,
        flexShrink: 0,
        "&.Mui-disabled": {
          minWidth: "0px",
          opacity: 0,
          width: 0,
          padding: 0,
          marginLeft: "-12px", // negative to offset parent's gap.
        },
        ".MuiButton-startIcon": {
          margin: 0,
        },
      }}
      startIcon={
        <span
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "44px",
          }}
        >
          <SendIcon width="24px" />
        </span>
      }
    />
  );
};

const compositionMenuStyle: CSSProperties = {
  position: "relative",
  width: "40px",
};

const compositionEditorStyle: CSSProperties = {
  position: "relative",
  flexGrow: 1,
  width: "10px",
};

const getCompositionSectionSx = (unpad: boolean, hover = false) => ({
  width: "100%",
  justifyContent: "center",
  alignItems: "flex-start",
  gap: "12px",
  ...(unpad
    ? {}
    : {
        padding:
          isStandaloneWebapp() && isMobileiOS()
            ? "16px 16px 30px 0px"
            : "16px 16px 16px 0px",
      }),
  ...(hover ? { backgroundColor: "#e8f4f8" } : {}),
});

export const formatDate = (date: Date): string => {
  const now = new Date();
  const options: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "numeric",
  }; // Time formatting options

  // Check if the date is today
  const isToday =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  if (isToday) {
    // If it's today, return the time only
    return date.toLocaleTimeString("en-US", options);
  } else {
    // Otherwise, return "Month Day, Time"
    const dateOptions: Intl.DateTimeFormatOptions = {
      month: "short",
      day: "numeric",
    };
    return `${date.toLocaleDateString(
      "en-US",
      dateOptions,
    )}, ${date.toLocaleTimeString("en-US", options)}`;
  }
};

export type SendOptions = {
  scheduledDate?: Date;
  attachments: PlainMessage<UploadAttachment>[];
  includeContactCard?: boolean;
};

interface CompositionSectionProps {
  placeholder: string;
  contactEnabled?: boolean;
  loading: boolean;
  thumbsUp: boolean;
  onSend: (
    inputValue: string,
    options: SendOptions,
    correlationId?: string,
  ) => Promise<boolean>;
  handleSendCannedMessage?: (
    message: string | null,
    includeContactCard?: boolean,
  ) => void;
  unpad?: boolean;
  autoFocus?: boolean;
  maxUploadBytes?: number;
  maxUploadBytesNonImage?: number;
  forceDisabled?: boolean;
  schedulingEnabled?: boolean;
  cacheKey?: string | null;
  maxContentLength?: number;
  initialContent?: string;
  onUpdateContent?: () => void;
}
export const CompositionSection = ({
  onSend,
  loading,
  placeholder,
  handleSendCannedMessage,
  thumbsUp = false,
  contactEnabled = true,
  schedulingEnabled = false,
  unpad = false,
  autoFocus = false,
  maxUploadBytes = 4900000,
  maxUploadBytesNonImage = undefined,
  forceDisabled = false,
  cacheKey = null,
  maxContentLength, // Add maxLength prop
  initialContent = "",
  onUpdateContent = () => {},
}: CompositionSectionProps) => {
  const currentUser = useContext(CurrentUserContext);
  const shouldLog = shouldLogForUser(currentUser?.ref);

  const isTouch = isTouchDevice();
  const editorRef = useRef<Handle>(null);
  const [triggerReset, setTriggerReset] = useState(false);
  const [content, setContent] = useCached<RichContent | null>(cacheKey, null);
  const [hover, setHover] = useState(false);
  const [schedulerOpen, setSchedulerOpen] = useState(false);
  const [scheduledMs, setScheduledMs] = useCached<number | undefined>(
    cacheKey ? `${cacheKey}:scheduled` : null,
    undefined,
  );
  const scheduled =
    scheduledMs !== undefined ? new Date(scheduledMs) : undefined;
  const setScheduled = (d: Date | undefined) =>
    setScheduledMs(d ? d.getTime() : undefined);

  const resetInput = () => {
    setTriggerReset(true);
    setContent(null);
    setScheduled(undefined);
    setSchedulerOpen(false);
  };

  const {
    onUpload,
    fileUploads,
    getCompleteAttachments,
    uploadPercentage,
    clearUploads,
    removeUpload,
    uploadsInProgress,
    updateFileUpload,
  } = useUploader({
    maxUploadBytes: maxUploadBytes,
    maxUploadBytesNonImage: maxUploadBytesNonImage,
  });

  const correlationIdRef = useRef<string | undefined>(undefined);

  const messageLength = twilioMessageLength(
    content?.text || initialContent || "",
  );
  const hasExceededMaxLength = !!(
    maxContentLength && messageLength > maxContentLength
  );

  const handleSend = async () => {
    const correlationId = shouldLog
      ? `send-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      : undefined;
    correlationIdRef.current = correlationId;

    if (shouldLog) {
      Sentry.logger.info("TextMessageCompose: handleSend called", {
        correlationId,
        userRef: currentUser?.ref,
        loading,
        uploadsInProgress,
        contentText: content?.text,
        contentLength: content?.text?.length,
        initialContent,
        fileUploadsCount: fileUploads.length,
        scheduled: scheduled?.toISOString(),
      });
    }

    // Check if there's a contact card in the uploads
    const hasContactCard = fileUploads.some(
      (upload) => upload.contentType === "__application/x-contact-card",
    );

    // Get all complete attachments, filtering out contact cards which are handled separately
    const allCompleteAttachments = getCompleteAttachments();
    const regularAttachments = allCompleteAttachments.filter((_, index) => {
      return fileUploads[index]?.contentType !== "__application/x-contact-card";
    });

    const attachments = [
      ...(content?.attachments || []),
      ...regularAttachments,
    ];

    if (loading || uploadsInProgress) {
      if (shouldLog) {
        Sentry.logger.info(
          "TextMessageCompose: Early return - loading or uploadsInProgress",
          { correlationId, loading, uploadsInProgress },
        );
      }
      return;
    }
    if (hasExceededMaxLength) {
      if (shouldLog) {
        Sentry.logger.info(
          "TextMessageCompose: Early return - exceeded max length",
          { correlationId, messageLength, maxContentLength },
        );
      }
      return;
    }
    if (
      !(content?.text || attachments.length || hasContactCard) &&
      !initialContent
    ) {
      if (shouldLog) {
        Sentry.logger.info(
          "TextMessageCompose: Early return - no content/attachments",
          {
            correlationId,
            hasContentText: !!content?.text,
            attachmentsLength: attachments.length,
            hasContactCard,
            hasInitialContent: !!initialContent,
          },
        );
      }
      return;
    }

    if (shouldLog) {
      Sentry.logger.info("TextMessageCompose: Calling onSend", {
        correlationId,
        messageContent: content?.text || initialContent || "",
        attachmentsCount: attachments.length,
        hasScheduledDate: !!scheduled,
        includeContactCard: hasContactCard,
      });
    }

    const success = await onSend(
      content?.text || initialContent || "",
      {
        attachments,
        scheduledDate: scheduled,
        includeContactCard: hasContactCard,
      },
      correlationId,
    );

    if (shouldLog) {
      Sentry.logger.info("TextMessageCompose: onSend completed", {
        correlationId,
        success,
      });
    }

    if (success) {
      clearUploads();
      resetInput();
    }
  };

  const addContactCard = () => {
    // Create a special "contact card" file upload item
    const contactCardUpload: FileUpload = {
      filename: "Contact Card",
      contentType: "__application/x-contact-card",
      size: 0,
      progress: 100,
      inlineReference: null,
    };

    // Check if contact card is already queued
    const hasContactCard = fileUploads.some(
      (upload) => upload.contentType === "__application/x-contact-card",
    );
    if (!hasContactCard) {
      const updatedUploads = [...fileUploads, contactCardUpload];
      // Update using the uploader's update function to maintain consistency
      updateFileUpload(updatedUploads);
    }
  };

  const onCannedMessage = (
    message: string | null,
    includeContactCard?: boolean,
  ) => {
    // If it's a contact card request, add it to the queue instead of sending immediately
    if (includeContactCard) {
      addContactCard();
      return;
    }

    // For other canned messages, only send if there's no current content
    if (!content?.text && !fileUploads.length) {
      handleSendCannedMessage &&
        handleSendCannedMessage(message, includeContactCard);
    }
  };

  useEffect(() => {
    if (!loading && autoFocus) {
      editorRef.current?.focus();
    }
  }, [loading]);

  let _initialContent = initialContent;
  if (
    content &&
    content.json &&
    // Handle case where content is empty
    content.json !== '{"type":"doc","content":[{"type":"paragraph"}]}'
  ) {
    try {
      _initialContent = JSON.parse(content.json);
    } catch (e) {
      console.error(`Error parsing initialContent: ${e}`);
    }
  }
  const disabled =
    loading ||
    (!content?.text && !fileUploads.length && !initialContent) ||
    uploadsInProgress ||
    hasExceededMaxLength;
  const isMobile = useIsMobile();

  return (
    <DragNDrop
      enabled={!(uploadsInProgress || loading || forceDisabled)}
      setHover={setHover}
      onUpload={(files) => onUpload(files)}
    >
      <Box display="flex" sx={getCompositionSectionSx(unpad, hover)}>
        <div style={{ ...compositionMenuStyle, height: "100%" }}>
          <MessageMenu disabled={!contactEnabled || forceDisabled}>
            {thumbsUp && (
              <SpeedDialAction
                icon={"👍"}
                tooltipPlacement="right"
                tooltipTitle={"Send 👍"}
                tooltipOpen={isTouch}
                onClick={() => onCannedMessage("👍", false)}
              />
            )}
            <UploadSpeedDialAction
              disabled={uploadsInProgress}
              icon={<Paperclip />}
              tooltipOpen={isTouch}
              onChange={(e) => {
                if (e.target.files) {
                  const filesArray = Array.from(e.target.files);
                  processImages(
                    filesArray,
                    null, // editor not needed for external handler
                    onUpload, // this won't be used since we have externalAttachmentHandler
                    () => {}, // setAttachments not needed for external handler
                    false, // inlineAttachmentsEnabled
                    onUpload, // externalAttachmentHandler - this will be called with processed files
                  );
                }
              }}
              tooltipPlacement="right"
              tooltipTitle={"Add an attachment"}
            />
            {schedulingEnabled && (
              <SpeedDialAction
                icon={<CalendarClock />}
                tooltipPlacement="right"
                tooltipTitle={"Schedule message"}
                tooltipOpen={isTouch}
                onClick={() => setSchedulerOpen(true)}
              />
            )}
            <SpeedDialAction
              icon={<SquareUserRound />}
              tooltipPlacement="right"
              tooltipTitle={"Add contact card"}
              tooltipOpen={isTouch}
              onClick={() => onCannedMessage(null, true)}
            />
          </MessageMenu>
          {uploadPercentage > 0 && (
            <LinearProgress
              variant="determinate"
              value={uploadPercentage}
              sx={{
                position: "absolute",
                bottom: "0px",
                width: "28px",
                left: "14px",
                boxShadow: "none",
              }}
            />
          )}
        </div>
        <div style={compositionEditorStyle}>
          {scheduled && (
            <Box
              display="flex"
              sx={{
                flexDirection: "row",
                gap: "6px",
                flexWrap: "wrap",
                marginBottom: "10px",
              }}
            >
              <Chip
                sx={{
                  backgroundColor: "#F2F2F2",
                  borderColor: "#E2E3E4",
                }}
                label={`Scheduled for ${formatDate(scheduled)}`}
                variant="outlined"
                deleteIcon={<X />}
                onClick={() => setSchedulerOpen(true)}
                onDelete={() => setScheduled(undefined)}
              />
            </Box>
          )}
          <SimpleEditor
            ref={editorRef}
            disabled={loading || !contactEnabled}
            placeholder={placeholder}
            initialContent={_initialContent}
            setContent={(content) => {
              setContent(content);
              onUpdateContent();
            }}
            attachmentsEnabled={true}
            onKeyDown={(_, e: KeyboardEvent) => {
              // Windows may send \r\n for Enter key
              if (
                (e.key === "Enter" || e.key === "\r") &&
                !e.shiftKey &&
                !isTouch
              ) {
                handleSend();
                return true;
              }
            }}
            inlineAttachmentsEnabled={false}
            attachmentActionEnabled={false}
            primaryAction={
              <SendButton
                onClick={handleSend}
                disabled={disabled || !contactEnabled || forceDisabled}
                shouldLog={shouldLog}
                debugContext={{
                  correlationId: correlationIdRef.current,
                  disabled,
                  contactEnabled,
                  forceDisabled,
                  loading,
                  uploadsInProgress,
                  hasContent: !!content?.text,
                  hasFileUploads: fileUploads.length > 0,
                  hasInitialContent: !!initialContent,
                  hasExceededMaxLength,
                }}
              />
            }
            triggerReset={triggerReset}
            onReset={() => {
              setTriggerReset(false);
            }}
            urlShortenerEnabled={true}
            isRichTextEnabled={false}
            externalAttachmentHandler={onUpload}
            inputSx={
              hasExceededMaxLength
                ? {
                    borderColor: "red",
                  }
                : {}
            }
          />
          {/* FileUploads */}
          {fileUploads.length > 0 && (
            <Box
              display="flex"
              sx={{
                flexDirection: "row",
                gap: "6px",
                flexWrap: "wrap",
                marginTop: isMobile ? "16px" : "12px",
              }}
            >
              {fileUploads.map((fileUpload, i) => (
                <UploadThumbnail
                  file={fileUpload}
                  removeUpload={removeUpload}
                  key={i}
                />
              ))}
            </Box>
          )}

          {/* Error Message for Max Length Exceeded */}
          {hasExceededMaxLength && (
            <Box
              display="flex"
              sx={{
                color: "red",
                fontSize: "12px",
                margin: "4px",
              }}
            >
              Message is too long to send ({messageLength} of {maxContentLength}{" "}
              characters). Note that emoji and special characters may count as
              multiple characters.
            </Box>
          )}
        </div>
      </Box>

      <ScheduledMessageDialog
        open={schedulerOpen}
        value={scheduled}
        onUpdate={async (d) => {
          setScheduled(d);
        }}
        onClose={() => {
          setSchedulerOpen(false);
        }}
      />
    </DragNDrop>
  );
};

export const ShellCompositionSection = ({
  onFocus,
  placeholder,
  disabled = false,
  unpad = false,
}: {
  onFocus: () => void;
  placeholder: string;
  disabled?: boolean;
  unpad?: boolean;
}) => {
  return (
    <Box display="flex" sx={getCompositionSectionSx(unpad)}>
      <div
        style={{
          ...compositionMenuStyle,
          marginLeft: "16px",
          width: "24px",
        }}
      >
        <MessageMenu disabled={true}>{""}</MessageMenu>
      </div>
      <div style={compositionEditorStyle} onFocusCapture={onFocus}>
        <SimpleEditor
          disabled={disabled}
          placeholder={placeholder}
          setContent={() => {}}
          inlineAttachmentsEnabled={false}
          attachmentActionEnabled={false}
          primaryAction={<SendButton onClick={() => {}} disabled={true} />}
          urlShortenerEnabled={true}
          isRichTextEnabled={false}
        />
      </div>
    </Box>
  );
};

export default ({
  familyRef,
  onSent,
  recipientRefs,
  placeholder,
  contactEnabled = true,
  forceDisabled = false,
  initialContent = "",
  onUpdateContent = () => {},
}: SMSInputProps) => {
  const currentUser = useContext(CurrentUserContext);
  const shouldLog = shouldLogForUser(currentUser?.ref);

  const callback = (resp: SendTextMessageResponse) => {
    const message = resp.textMessage;
    onSent(
      new FeedEntry({
        ref: message.ref,
        senderRef: message.senderRef,
        timestampSec: message.timestampSec,
        medium: Medium.SMS,
        textMessage: message,
        recipientRefs: recipientRefs,
      }),
    );
  };
  const {
    loading,
    request,
    error: requestError,
    errorCode,
  } = useSendTextMessage();
  const handleSend = async (
    inputValue: string,
    options: SendOptions,
    correlationId?: string,
  ) => {
    if (shouldLog) {
      Sentry.logger.info(
        "TextMessageCompose: Main handleSend called (wrapper)",
        {
          correlationId,
          userRef: currentUser?.ref,
          familyRef,
          recipientRefs,
          loading,
          inputValueLength: inputValue.length,
          hasScheduledDate: !!options.scheduledDate,
          includeContactCard: options.includeContactCard,
          attachmentsCount: options.attachments.length,
        },
      );
    }

    if (loading) {
      if (shouldLog) {
        Sentry.logger.info(
          "TextMessageCompose: Early return - already loading",
          { correlationId },
        );
      }
      return false;
    }

    const resp = await request(
      new SendTextMessageRequest({
        familyRef: familyRef || "",
        memberRefs: recipientRefs,
        content: inputValue,
        includeContactCard: options.includeContactCard || false,
        attachments: options.attachments,
        scheduledSendSecUTC: BigInt(
          Math.ceil((options.scheduledDate?.getTime() || 0) / 1000),
        ),
      }),
    );

    if (shouldLog) {
      Sentry.logger.info("TextMessageCompose: Request completed", {
        correlationId,
        success: !!resp,
        hasResponse: !!resp,
        error: requestError,
        errorCode: errorCode,
      });
    }

    // If request failed (returns null), log the error details
    if (!resp && shouldLog) {
      Sentry.logger.error("TextMessageCompose: Request failed", {
        correlationId,
        context: "Main handleSend wrapper",
        userRef: currentUser?.ref,
        familyRef,
        recipientRefs,
        inputValueLength: inputValue.length,
        error: requestError,
        errorCode: errorCode,
      });
    }

    if (resp) {
      callback(resp);
      return true;
    }
    return false;
  };
  const handleSendCannedMessage = async (
    message: string | null,
    includeContactCard?: boolean,
  ) => {
    const resp = await request(
      new SendTextMessageRequest({
        familyRef: familyRef || "",
        memberRefs: recipientRefs,
        content: message || "",
        includeContactCard: includeContactCard || false,
        attachments: [],
        scheduledSendSecUTC: BigInt(0),
      }),
    );
    if (resp) {
      callback(resp);
      return true;
    }
    return false;
  };

  return (
    <CompositionSection
      contactEnabled={contactEnabled}
      onSend={handleSend}
      placeholder={placeholder}
      handleSendCannedMessage={handleSendCannedMessage}
      loading={loading}
      thumbsUp={true}
      forceDisabled={forceDisabled}
      schedulingEnabled={true}
      cacheKey={
        familyRef
          ? `sms-compose-family:${familyRef}`
          : `sms-compose-contacts:${recipientRefs.join("|")}`
      }
      // Twilio has a lower limit for non-images
      // https://help.twilio.com/articles/360018832773-Twilio-Programmable-SMS-Supported-File-Types-and-Size-Limits-for-MMS-Media-Messages#h_01HBGHJ4R9BKZCF9QVYTCSARSM
      maxUploadBytesNonImage={1000000}
      maxContentLength={1599} // Twilio max is 1600
      initialContent={initialContent}
      onUpdateContent={onUpdateContent}
    />
  );
};
